import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hotelesOpciones, logos } from "../../../hook/Arreglos";
import { obtenerListaEmpresas } from "../../../store/Empresa/thunkEmpresa";
import { traerTodoslosClientesSegunlaempresa } from "../../../store/clientes/thunkClientes";
import { crearCotizacion, obtenerNumeroConsecutivo } from "../../../store/cotizaciones/thunkCotizaciones";
import { UnificarModales } from "../Modales/UnificarModales";
import { EjecutivoInfo } from "./VistasCotizacion/EjecutivoInfo";
import { ClienteInfo } from "./VistasCotizacion/ClienteInfo";
import { CotizacionInfo } from "./VistasCotizacion/CotizacionInfo";
import { CalendarioSeleccion } from "./VistasCotizacion/CalendarioSeleccion";
import { TablaRequerimientos } from "./VistasCotizacion/TablaRequerimientos";
import { ResumenTotalesRequerimientos } from "./VistasCotizacion/ResumenTotales";
import { TablaOtrosServicios } from "./VistasCotizacion/TablaOtrosServicios";
import { ResumenTotalesOtroServicios } from "./VistasCotizacion/ResumenTotalesOtroServicios";
import { TablaHabitaciones } from "./VistasCotizacion/TablaHabitaciones";
import { ResumenHabitaciones } from "./VistasCotizacion/ResumenHabitaciones";
import { TotalGlobal } from "./VistasCotizacion/TotalGlobal";
import { TotalGlobalModal } from "./VistasCotizacion/TotalGlobalModal";
import { NotaCotizacion } from "./VistasCotizacion/NotaCotizacion";
import { extarerEventoToken } from "../../../store/Eventos/thunkEventos";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

/**
 * Función para formatear una fecha a un formato legible en español.
 * Devuelve la fecha con detalles como día de la semana, mes, año, hora y minutos.
 */
const formatearFecha = (fecha) =>
    fecha
        ? new Date(fecha).toLocaleString("es-ES", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
              year: "numeric",
              month: "long",
              day: "numeric",
              weekday: "long",
          })
        : "N/D";

/**
 * Componente principal para la creación de cotizaciones.
 * Este componente gestiona el estado global de la cotización, las llamadas a la API y la lógica
 * para seleccionar empresas, clientes, fechas, hoteles y requerimientos.
 */
export const CrearCotizacion = () => {
    const navigate = useNavigate();
    // Hook de Redux para despachar acciones y obtener estado global.
    const dispatch = useDispatch();
    const { name_admin, puesto, email_admin, phone_admin } = useSelector((state) => state.auth);

    // Estado local para manejo de opciones y datos auxiliares
    const [empresasOpciones, setEmpresasOpciones] = useState([]);
    const [clientesOpciones, setClientesOpciones] = useState([]);
    const [todosLosclientes, setTodosLosclientes] = useState([]);
    const [preloadCliente, setPreloadCliente] = useState(false);
    const [mostrarCalendario, setMostrarCalendario] = useState(false);

    // Estado principal del formulario que contiene toda la información necesaria para la cotización
    const [formulario, setFormulario] = useState({
        fechaCreacion: formatearFecha(new Date()),
        datosCliente: {},
        consecutivo: "",
        nombreEvento: "",
        MonedaTipo: [
            { id: 1, nombre: "$", estado: true },
            { id: 2, nombre: "¢", estado: false },
        ],
        hotelSeleccionado: "",
        empresaSeleccionada: "",
        fechaSeleccionadas: [],
        requerimientos: [],
        otroServicios: [],
        requerimientoshabitaciones: [],
        totalRequerimientos: {
            subtotal: 0,
            impuestoVenta: 0,
            impuestoServicio: 0,
            totalAB: 0,
        },
        totalOtroServicios: {
            subtotal: 0,
            impuestoVenta: 0,
            impuestoServicio: 0,
            totalAB: 0,
        },
        totalHabitaciones: {
            subtotal: 0,
            impuestoVenta: 0,
            impuestoServicio: 0,
            totalAB: 0,
        },
        totalGlobalCotizacion: 0,
        notaCoti: "",
        tokenEvento: "",
    });

    /**
     * Función para cambiar el símbolo de la moneda utilizada.
     * Alterna entre dólares y colones.
     */
    const cambiarSimbolo = () => {
        setFormulario((prev) => ({
            ...prev,
            MonedaTipo: prev.MonedaTipo.map((moneda) =>
                moneda.id === 2
                    ? {
                          ...moneda,
                          estado: !prev.MonedaTipo.find((m) => m.id === 2).estado,
                      }
                    : { ...moneda, estado: !moneda.estado },
            ),
        }));
    };

    /**
     * Obtiene el símbolo de la moneda activa (la que tenga estado: true).
     * Por defecto es "$" si no se encuentra otra activada.
     */
    const monedaActiva = formulario.MonedaTipo.find((moneda) => moneda.estado)?.nombre || "$";

    /**
     * Asigna el logo del hotel según el ID seleccionado.
     * Si no se encuentra coincidencia, se muestra un logo genérico.
     */
    const asignarLogo = (idHotel) => {
        const hotel = logos.find((item) => item.id === Number(idHotel));
        return hotel ? hotel.logo : "/assets/logo2.jpg";
    };

    /**
     * Obtiene la lista de empresas desde la API mediante un thunk y las
     * asigna a las opciones del select correspondiente.
     * También, por ejemplificación, establece un par de fechas preseleccionadas.
     */
    const fetchEmpresa = useCallback(async () => {
        try {
            const empresasData = await dispatch(obtenerListaEmpresas());
            const opcionesEmpresas = empresasData.map(({ id_empresa, nombre_empresa }) => ({
                value: id_empresa,
                label: nombre_empresa,
            }));
            setEmpresasOpciones(opcionesEmpresas);
        } catch (error) {
            console.error("Error al obtener empresas:", error);
        }
    }, [dispatch]);

    // Llama a fetchEmpresa al montar el componente para precargar las empresas.
    useEffect(() => {
        fetchEmpresa();
    }, [fetchEmpresa]);

    // Hook para extraer el parámetro "data" de la URL
    const getIdFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("data");
    }, [location.search]);

    async function validarIdHotel(datos) {
        // Extraemos los valores de id_hotel de todos los objetos en el arreglo
        const idHoteles = datos.map((dato) => dato.id_hotel);

        // Verificamos si todos los IDs de hotel son iguales
        const todosIguales = idHoteles.every((id) => id === idHoteles[0]);

        // Si todos son iguales, retornamos el primer valor; de lo contrario, retornamos 200
        return todosIguales ? idHoteles[0] : 200;
    }
    async function formaterFecha(datos) {
        // Usar un Set para eliminar duplicados
        const fechasUnicas = new Set(
            datos.map((item) => {
                const fechaCompleta = item.fech_Inicio_evento;
                // Remover todo después del espacio o "T"
                const soloFecha = fechaCompleta.split(/[T\s]/)[0];
                return soloFecha; // Devuelve la fecha como string sin comillas
            }),
        );
        return Array.from(fechasUnicas); // Convertir el Set a un arreglo
    }

    const extraerDatosEvento = useCallback(async () => {
        try {
            // Obtener el token desde la URL
            const token = getIdFromUrl();

            // Validar el token obtenido
            if (!token || token === "0") {
                console.log("Token no válido o igual a 0. Proceso detenido.");
                return; // Detener la ejecución si el token es inválido
            }

            // Obtener los datos del evento usando el token
            const eventosData = await dispatch(extarerEventoToken(token));

            // Extraer el ID de la empresa del evento y validar el hotel asociado
            const idEmpresaEvento = eventosData.data["0"].idEmpresaEvento;
            const hotelSelec = await validarIdHotel(eventosData.data);
            const nombreEvento = eventosData.data["0"].nombre_evento;

            // Actualizar el formulario con el ID de la empresa y el hotel seleccionado
            setFormulario((prev) => ({
                ...prev,
                empresaSeleccionada: idEmpresaEvento,
                hotelSeleccionado: hotelSelec,
                nombreEvento: nombreEvento,
                tokenEvento: token,
            }));

            // Ejecutar una función adicional relacionada con la empresa seleccionada
            await ejecutarFuncionAdicional(idEmpresaEvento);

            // Extraer el ID del cliente asociado al evento
            const idClienteEvento = eventosData.data["0"].id_cleinteev;

            // Actualizar el formulario con el cliente seleccionado
            setFormulario((prev) => ({
                ...prev,
                clienteSeleccionado: idClienteEvento,
            }));

            const resultado = await formaterFecha(eventosData.data);
            const fechas = resultado; // Ahora es un arreglo de strings
            const fechaSeleccionadas = fechas.map((f) => new Date(`${f}T00:00:00`));
            setFormulario((prev) => ({ ...prev, fechaSeleccionadas }));
            setMostrarCalendario(true);
        } catch (error) {
            // Manejar errores durante la extracción de datos
            console.error("Error al obtener los datos del evento:", error);
        }
    }, [dispatch, getIdFromUrl]);

    // Ejecutar la extracción de datos del evento cuando existan opciones de empresas
    useEffect(() => {
        if (empresasOpciones.length > 0) {
            extraerDatosEvento();
        }
    }, [empresasOpciones, extraerDatosEvento]);

    useEffect(() => {
        if (clientesOpciones.length > 0) {
            seleccionarCliente(formulario.clienteSeleccionado);
        }
    }, [clientesOpciones]);



    /**
     * Obtiene el número consecutivo actual de cotizaciones desde la API,
     * genera un identificador único, y asigna el consecutivo completo al formulario.
     *
     * @returns {Promise<void>} - No retorna ningún valor. Actualiza el estado del formulario.
     */
    const obtenerYAsignarConsecutivo = useCallback(async () => {
        try {
            // Despacha una acción para obtener el número consecutivo actual de cotizaciones desde la API.
            // `obtenerNumeroConsecutivo` es una acción asíncrona que probablemente realiza una llamada al backend.
            const { totalCotizaciones } = await dispatch(obtenerNumeroConsecutivo());

            // Genera un identificador único utilizando una función externa.
            // `generarIdentificador` debe ser una función que retorna un string único, por ejemplo, un UUID o similar.
            const identificador = generarIdentificador();

            // Calcula el nuevo número consecutivo sumando 110 al total actual de cotizaciones.
            // **Nota**: El valor 110 parece ser un "offset" o incremento específico. Asegúrate de que este valor es intencional y documentado.
            const nuevoConsecutivo = parseInt(totalCotizaciones, 10) + 110;

            // Actualiza el estado del formulario agregando el nuevo consecutivo.
            // Combina el `nuevoConsecutivo` con el `identificador` para formar un identificador único para la nueva cotización.
            setFormulario((prev) => ({
                ...prev,
                consecutivo: `${nuevoConsecutivo}${identificador}`,
            }));
        } catch (error) {
            // Registra el error en la consola para propósitos de diagnóstico.
            console.error("Error al obtener número consecutivo:", error);

            // **Recomendación Adicional**:
            // Considera informar al usuario sobre el error mediante una notificación visual.
            // Por ejemplo, utilizando SweetAlert2 (Swal):
            // Swal.fire({
            //     title: "Error",
            //     text: "No se pudo obtener el número consecutivo. Por favor, inténtelo nuevamente.",
            //     icon: "error",
            // });
        }
    }, [dispatch]); // Dependencias del useCallback. Se actualiza si `dispatch` cambia.

    /**
     * Hook que se ejecuta al montar el componente para obtener y asignar el número consecutivo inicial.
     * Utiliza `useEffect` para llamar a `obtenerYAsignarConsecutivo` una vez que el componente ha sido renderizado.
     */
    useEffect(() => {
        obtenerYAsignarConsecutivo(); // Llama a la función para establecer el consecutivo al montar el componente.
    }, [obtenerYAsignarConsecutivo]); // Dependencia: se ejecuta nuevamente si `obtenerYAsignarConsecutivo` cambia.

    /**
     * Dada la empresa seleccionada, carga todos los clientes asociados a esa empresa
     * y actualiza las opciones del select de clientes.
     *
     * @param {string} selected - El identificador de la empresa seleccionada.
     */
    const ejecutarFuncionAdicional = async (selected) => {
        try {
            // Reinicia los datos del cliente en el formulario seleccionando una nueva empresa.
            setFormulario((prev) => ({ ...prev, datosCliente: "" }));

            // Activa el estado de preloader para indicar que se está cargando la información de clientes.
            setPreloadCliente(true);

            // Despacha una acción para traer todos los clientes asociados a la empresa seleccionada.
            // `traerTodoslosClientesSegunlaempresa` es una acción asíncrona que probablemente hace una llamada al backend.
            const leadData = await dispatch(traerTodoslosClientesSegunlaempresa(selected));

            // Mapea los datos de los clientes recibidos para crear las opciones del select de clientes.
            // Cada opción tiene un valor (`value`) que corresponde al ID del cliente y una etiqueta (`label`) que muestra el nombre del cliente.
            const options = leadData.map(({ id_cliente, nombre_cliente }) => ({
                value: id_cliente,
                label: nombre_cliente,
            }));

            // Actualiza el estado de las opciones de clientes para que el componente select las muestre.
            setClientesOpciones(options);

            // Guarda todos los clientes obtenidos en el estado `todosLosclientes` para usos futuros o referencias.
            setTodosLosclientes(leadData);

            // Desactiva el estado de preloader una vez que la carga de clientes ha finalizado.
            setPreloadCliente(false);
        } catch (error) {
            // Registra el error en la consola para propósitos de diagnóstico.
            console.error("Error al cargar los clientes:", error);

            // Opcional: Puedes agregar una notificación al usuario informando sobre el error.
            // Por ejemplo, usando Swal:
            // Swal.fire({
            //     title: "Error",
            //     text: "No se pudieron cargar los clientes. Por favor, inténtelo nuevamente.",
            //     icon: "error",
            // });

            // Asegura que el preloader se desactive incluso si ocurre un error.
            setPreloadCliente(false);
        }
    };

    /**
     * Asigna el cliente seleccionado al formulario.
     * Esto permite mostrar la información del cliente (correo, teléfono, etc.).
     */
    const seleccionarCliente = async (selected) => {
        if (!selected) return;
        const clienteSeleccionado = todosLosclientes.find((cli) => cli.id_cliente === Number(selected));

        if (clienteSeleccionado) {
            setFormulario((prev) => ({
                ...prev,
                datosCliente: clienteSeleccionado,
            }));
        }
    };

    /**
     * Maneja la selección de fechas desde el componente de calendario.
     * Almacena las fechas seleccionadas en el estado del formulario.
     */
    const handleSelect = (selectedDays) => {
        setFormulario((prev) => ({
            ...prev,
            fechaSeleccionadas: selectedDays || [],
        }));
    };

    /**
     * Elimina una fecha específica del arreglo de fechas seleccionadas.
     */
    const eliminarFecha = (fecha) => {
        setFormulario((prev) => ({
            ...prev,
            fechaSeleccionadas: prev.fechaSeleccionadas.filter((d) => d.getTime() !== fecha.getTime()),
        }));
    };

    /**
     * Maneja el envío del formulario para crear una estimación.
     * Realiza validaciones, muestra mensajes de alerta y gestiona la creación de la cotización.
     * @param {Event} e - Evento de envío del formulario.
     */
    const handleSubmit = (e) => {
        e.preventDefault(); // Evita que el formulario realice su acción por defecto al enviarse.

        console.log("Formulario a enviar:", formulario); // Muestra el estado actual del formulario en la consola.
        // Validación: Verifica que se haya seleccionado un hotel.
        if (formulario.hotelSeleccionado === "") {
            Swal.fire({
                title: "Error",
                text: "Seleccione un hotel para continuar.",
                icon: "error",
            });
            return; // Detiene la ejecución si no se cumple la validación.
        }

        if (formulario.nombreEvento === "") {
            Swal.fire({
                title: "Error",
                text: "Nombre del evento es requerido.",
                icon: "error",
            });
            return; // Detiene la ejecución si no se cumple la validación.
        }

        // Validación: Verifica que se hayan ingresado los datos del cliente.
        if (Object.keys(formulario.datosCliente).length === 0) {
            Swal.fire({
                title: "Error",
                text: "Seleccione un cliente para continuar.",
                icon: "error",
            });
            return; // Detiene la ejecución si no se cumple la validación.
        }

        // Mostrar mensaje de confirmación al usuario antes de proceder con la creación.
        Swal.fire({
            title: "¿Estás seguro?",
            text: "Por favor, verifica bien los datos antes de crear la estimación.",
            icon: "warning",
            showCancelButton: true, // Muestra el botón de cancelación.
            confirmButtonText: "Sí, crear", // Texto del botón de confirmación.
            cancelButtonText: "Cancelar", // Texto del botón de cancelación.
        }).then(async (result) => {
            // Verifica si el usuario confirmó la acción.
            if (result.isConfirmed) {
                // Muestra un mensaje con un preloader indicando que el proceso está en curso.
                Swal.fire({
                    title: "Creando la estimación...",
                    text: "Por favor, espere mientras termina el proceso.",
                    icon: "info",
                    allowOutsideClick: false, // Evita que el usuario cierre el modal haciendo clic afuera.
                    allowEscapeKey: false, // Evita que el usuario cierre el modal usando la tecla Escape.
                    didOpen: () => {
                        Swal.showLoading(); // Muestra una animación de carga.
                    },
                });

                try {
                    // Formatea las fechas seleccionadas en el formulario.
                    const fechasFormateadas = formulario.fechaSeleccionadas
                        .sort((a, b) => a - b) // Ordena las fechas de manera cronológica.
                        .map((fecha) => {
                            // Convierte cada fecha al formato 'YYYY-MM-DD'.
                            const year = fecha.getFullYear();
                            const month = String(fecha.getMonth() + 1).padStart(2, "0"); // Mes con dos dígitos.
                            const day = String(fecha.getDate()).padStart(2, "0"); // Día con dos dígitos.
                            return `${year}-${month}-${day}`;
                        });

                    // Despacha la acción para crear la cotización y espera su finalización.
                    // `crearCotizacion` es una acción asíncrona que envía los datos al backend.
                    const result = await dispatch(crearCotizacion(formulario, fechasFormateadas, mostrarCalendario));

                    // Verifica si la creación de la cotización fue exitosa mediante el ID retornado.
                    if (result.data.cotizacionId <= 0) {
                        Swal.fire({
                            title: "Error",
                            text: "Ocurrió un problema al crear la estimación. Inténtelo nuevamente.",
                            icon: "error",
                        });
                        return; // Detiene la ejecución si hubo un error en la creación.
                    }

                    // Muestra un mensaje de éxito indicando que la cotización se creó correctamente.
                    Swal.fire({
                        title: "¡Estimación creada!",
                        text: "La estimación ha sido creada con éxito.",
                        icon: "success",
                        timer: 4000, // Cierra el mensaje automáticamente después de 4 segundos.
                        showConfirmButton: false, // Oculta el botón de confirmación.
                    }).then(() => {

                        navigate(`/cotizaciones/view?data=${result.data.cotizacionId}`); // Redirige a la página de creación
                    });
                } catch (error) {
                    // Registra el error en la consola para diagnóstico.
                    console.error("Error al crear la estimación:", error);

                    // Muestra un mensaje de error al usuario indicando que algo salió mal.
                    Swal.fire({
                        title: "Error",
                        text: "Ocurrió un problema al crear la estimación. Inténtelo nuevamente.",
                        icon: "error",
                    });
                }
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                // Si el usuario cancela la acción, muestra un mensaje indicando que no se creó la estimación.
                Swal.fire("Cancelado", "No se creó la estimación.", "error");
            }
        });
    };

    return (
        <>
            <div className="alert alert-success d-block text-center text-uppercase">
                <i className="feather icon-check-circle me-2"></i>Usted esta en Crear Cotizacion
            </div>
            {/* Componente para manejar el cambio de moneda y mostrar la moneda activa */}
            <div className="mb-3">
                <div className="form-check form-switch">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        onChange={cambiarSimbolo}
                        checked={formulario.MonedaTipo.find((m) => m.id === 2)?.estado}
                        style={{ transform: "scale(1.5)" }}
                    />
                    <label className="form-check-label ms-2" style={{ fontSize: "1.2rem" }}>
                        Transformar a {monedaActiva === "¢" ? "dólares" : "colones"}
                    </label>
                </div>
                <h4>Símbolo de moneda actual:</h4>
                <h3>{monedaActiva}</h3>
            </div>

            {/* Formulario principal de la cotización */}
            <form onSubmit={handleSubmit} autoComplete="off">
                <div className="d-flex align-items-center justify-content-between py-3 card-header">
                    {/* Botón que abre un modal para acciones adicionales, integrándose con la lógica de fetchEmpresa */}
                    <h5 className="mb-0"></h5>
                    <button className="btn btn-sm btn-link-dark">
                        <UnificarModales functions={fetchEmpresa} />
                    </button>
                </div>

                <div className="g-3 row">
                    {/* Encabezado con el logo del hotel y el consecutivo */}
                    <div className="col-12">
                        <div className="align-items-center g-3 row">
                            <div className="col-sm-6">
                                <div className="d-flex align-items-center mb-2">
                                    <img
                                        alt="images"
                                        loading="lazy"
                                        width="160"
                                        height="28"
                                        decoding="async"
                                        className="img-fluid"
                                        src={asignarLogo(formulario.hotelSeleccionado)}
                                    />
                                </div>
                                <h2 className="mb-0">#{formulario.consecutivo}</h2>
                            </div>
                        </div>

                        <br />
                        <br />
                        <TotalGlobal formulario={formulario} setFormulario={setFormulario} monedaActiva={monedaActiva} />
                        {/* Selector de hotel */}
                        <div className="col-xl-3 col-sm-6">
                            <label className="form-label">Seleccione el hotel *</label>
                            <select
                                className="form-control"
                                value={formulario.hotelSeleccionado}
                                onChange={(e) =>
                                    setFormulario((prev) => ({
                                        ...prev,
                                        hotelSeleccionado: e.target.value,
                                    }))
                                }
                            >
                                <option value="">Seleccione el Hotel</option>
                                {hotelesOpciones.map((h) => (
                                    <option key={h.value} value={h.value}>
                                        {h.label}
                                    </option>
                                ))}
                                <option value="200">Todos los hoteles</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ textAlign: "center", backgroundColor: "#000", color: "#fff" }} className="alert alert-secondary" role="alert">
                        INFORMACIÓN DE LA COTIZACIÓN
                    </div>

                    {/* Información del Ejecutivo (nombre, puesto, email, teléfono) */}
                    <div className="col-sm-4">
                        <EjecutivoInfo name_admin={name_admin} puesto={puesto} email_admin={email_admin} phone_admin={phone_admin} />
                    </div>

                    {/* Información del Cliente (selección de empresa, cliente, correo, teléfono) */}
                    <div className="col-sm-4">
                        <ClienteInfo
                            empresasOpciones={empresasOpciones}
                            clientesOpciones={clientesOpciones}
                            empresaSeleccionada={formulario.empresaSeleccionada}
                            clienteSeleccionado={formulario.clienteSeleccionado}
                            formulario={formulario}
                            preloadCliente={preloadCliente}
                            ejecutarFuncionAdicional={ejecutarFuncionAdicional}
                            seleccionarCliente={seleccionarCliente}
                            setFormulario={setFormulario}
                        />
                    </div>

                    {/* Información de la Cotización (fecha, nombre del evento y toggle del calendario) */}
                    <div className="col-sm-4">
                        <CotizacionInfo
                            formulario={formulario}
                            setFormulario={setFormulario}
                            fechaCreacion={formulario.fechaCreacion}
                            setMostrarCalendario={setMostrarCalendario}
                        />
                    </div>

                    {/* Selección de Fechas mediante el Calendario */}
                    <div className="col-sm-12">
                        <CalendarioSeleccion
                            mostrarCalendario={mostrarCalendario}
                            fechaSeleccionadas={formulario.fechaSeleccionadas}
                            handleSelect={handleSelect}
                            eliminarFecha={eliminarFecha}
                        />
                    </div>

                    {/* Tabla de Requerimientos asociados a la cotización (servicios, productos, cantidades, etc.) */}
                    <TablaRequerimientos formulario={formulario} setFormulario={setFormulario} hotelesOpciones={hotelesOpciones} />
                    {/* Resumen de Totales de la Cotización (subtotales, descuentos, impuestos y total) */}
                    <div className="col-12">
                        <ResumenTotalesRequerimientos formulario={formulario} setFormulario={setFormulario} monedaActiva={monedaActiva} />
                    </div>
                    <TablaOtrosServicios formulario={formulario} setFormulario={setFormulario} hotelesOpciones={hotelesOpciones} />
                    <div className="col-12">
                        <ResumenTotalesOtroServicios formulario={formulario} setFormulario={setFormulario} monedaActiva={monedaActiva} />
                    </div>
                    <TablaHabitaciones formulario={formulario} setFormulario={setFormulario} hotelesOpciones={hotelesOpciones} />
                    <div className="col-12">
                        <ResumenHabitaciones formulario={formulario} setFormulario={setFormulario} monedaActiva={monedaActiva} />
                    </div>
                    <div className="col-12">
                        <div style={{ textAlign: "center", backgroundColor: "#000", color: "#fff" }} className="alert alert-secondary" role="alert">
                            INGRESE LA NOTA PARA ESTA COTIZACIÓN
                        </div>
                        <NotaCotizacion formulario={formulario} setFormulario={setFormulario} monedaActiva={monedaActiva} />
                    </div>
                    <div className="col-12">
                        <div style={{ textAlign: "center", backgroundColor: "#000", color: "#fff" }} className="alert alert-secondary" role="alert">
                            TOTALES GENERAL
                        </div>
                        <TotalGlobal formulario={formulario} setFormulario={setFormulario} monedaActiva={monedaActiva} />
                    </div>
                </div>
                <br />
                <br />

                {/* Botón para crear la cotización, al hacer submit del formulario */}
                <div className="d-print-none align-items-center justify-content-end row">
                    <div className="col-auto btn-page col">
                        <button type="submit" className="btn-print-invoice btn btn-dark" id="btn-crear-cotizacion">
                            Crear Cotización
                        </button>
                    </div>
                </div>
            </form>
        </>
    );
};

/**
 * Genera un identificador de 3 caracteres alfabéticos aleatorios.
 * Esto se utiliza para complementar el número consecutivo de la cotización.
 */
function generarIdentificador() {
    return Array(3)
        .fill(null)
        .map(() => String.fromCharCode(65 + Math.floor(Math.random() * 26)))
        .join("");
}
